import { useRef, useState } from "react"
import AdditionalSettings from "./AdditionalSettings"
import FamilyPicker from "./FamilyPicker"
import KnownPains from "./KnownPains"
import TemplatePicker from "./TemplatePicker"
import { RULES_COPY } from "@/content/sessionRules"
import { TEMPLATES, templateApplies } from "@/content/templates"
import type { SessionTemplate } from "@/content/templates"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { setFamilyInPlay, updateSessionConfig } from "@/services/sessions"
import { useSessionStore } from "@/store/sessionStore"
import { activeRounds, selectSession } from "@/store/selectors"
import type { SessionConfig } from "@/types"
import styles from "@/styles/components/SessionRules.module.css"

/*
 * The facilitator's panel beside the lobby table: the rules the session runs
 * under, editable until it starts.
 *
 * Composition only. Every control writes straight to `session.config`, so a
 * participant's screen reflects a change as soon as it lands — there is no local
 * draft to reconcile. What each section says lives in `content/sessionRules`.
 */
export default function SessionRules() {
  const session = useSessionStore(selectSession)
  const { write, isPending } = useFirestoreWrite()
  const [openRounds, setOpenRounds] = useState(false)
  const [openExercises, setOpenExercises] = useState(false)
  const [openFamilies, setOpenFamilies] = useState(false)
  const [pickedCustom, setPickedCustom] = useState(false)
  const group = useRef<HTMLDivElement>(null)

  if (!session) return null

  const config = session.config

  /*
   * Only the decisive controls are keyed — a template tile and an exercise
   * toggle, each one press with a whole round list behind it.
   *
   * The steppers deliberately are not. Retiming a round is five presses on the
   * same button, and a spinner in place of the − glyph for each of them would
   * flicker through the burst while telling the facilitator nothing they cannot
   * already see in the number beside it. Same for the family checkboxes.
   */
  const change = (patch: Partial<SessionConfig>, key?: string) =>
    write(() => updateSessionConfig(session.id, patch), key)

  const plannedMinutes = activeRounds(config).reduce(
    (total, round) => total + config.roundDurations[round],
    0
  )

  const matched = TEMPLATES.find((template) => templateApplies(template, config)) ?? null
  const active = pickedCustom ? null : matched

  const applyTemplate = (template: SessionTemplate) => {
    setPickedCustom(false)
    change(template.config, template.key)
  }

  const applying = TEMPLATES.find((template) => isPending(template.key))?.key ?? null

  /*
   * Custom writes nothing — it opens the rules that got the config here. When
   * they are open already there is nothing to open, so the cursor goes to the
   * first control instead; otherwise the tile looks broken on the second press.
   */
  const chooseCustom = () => {
    const alreadyOpen = openRounds && openExercises
    setPickedCustom(true)
    setOpenRounds(true)
    setOpenExercises(true)
    if (alreadyOpen) group.current?.querySelector<HTMLButtonElement>("[data-stepper]")?.focus()
  }

  return (
    <aside className={styles.panel} aria-label={RULES_COPY.heading}>
      <h2 className={styles.heading}>{RULES_COPY.heading}</h2>

      <KnownPains sessionId={session.id} notes={session.knownPains} />

      <TemplatePicker
        active={active}
        plannedMinutes={plannedMinutes}
        applying={applying}
        onPick={applyTemplate}
        onPickCustom={chooseCustom}
      />

      <AdditionalSettings
        config={config}
        openRounds={openRounds}
        openExercises={openExercises}
        onToggleRounds={() => setOpenRounds((open) => !open)}
        onToggleExercises={() => setOpenExercises((open) => !open)}
        onChange={change}
        isPending={isPending}
        groupRef={group}
      />

      {/* No key: the deck is chosen by ticking through the family checkboxes, and
          a spinner per tick would be noise over a list that already shows the
          answer. */}
      <FamilyPicker
        config={config}
        open={openFamilies}
        onToggle={() => setOpenFamilies((open) => !open)}
        onPick={(family, inPlay) => write(() => setFamilyInPlay(session.id, family, inPlay))}
      />
    </aside>
  )
}
